import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, type Workspace } from "../lib/api";
import { LoadingButton } from "./Spinner";
import { useConfirm } from "./ConfirmDialog";
import { writeActiveWorkspaceId } from "./Layout";
import Avatar from "./Avatar";

interface Props {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
}

// Modal version of the workspace settings page. Lets the user rename the
// workspace, manage its logo, members, and invites, and (for owners)
// delete it — all without leaving the current request.
export default function WorkspaceSettingsModal({ workspace, open, onClose }: Props) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const confirm = useConfirm();

  const [name, setName] = useState(workspace.name);
  // Re-sync the name field if the workspace changes while the modal is
  // open (e.g. user switched workspaces with the modal still mounted).
  useEffect(() => { setName(workspace.name); }, [workspace.id, workspace.name]);

  const rename = useMutation({
    mutationFn: () => api.workspaces.rename(workspace.id, name.trim() || workspace.name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });

  const removeWs = useMutation({
    mutationFn: () => api.workspaces.remove(workspace.id),
    onSuccess: () => {
      writeActiveWorkspaceId(null);
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      onClose();
      nav("/");
    },
  });

  // Escape closes the modal — matching the other modals' behavior.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-[color:var(--backdrop)]"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-3xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Workspace settings</h2>
            <span className="text-xs muted-strong">— {workspace.name}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto scroll-pane p-5 space-y-5 bg-bg">
          <WorkspaceLogoCard workspace={workspace} />

          <form
            className="card p-5 space-y-3"
            onSubmit={(e) => { e.preventDefault(); rename.mutate(); }}
          >
            <h3 className="font-semibold">Details</h3>
            <div className="space-y-2">
              <label className="label">Workspace name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} disabled={workspace.role !== "owner"} />
            </div>
            {workspace.role === "owner" && (
              <div className="flex justify-end">
                <LoadingButton type="submit" className="btn-primary" pending={rename.isPending} pendingLabel="Saving…">
                  Save
                </LoadingButton>
              </div>
            )}
          </form>

          <MembersCard workspaceId={workspace.id} role={workspace.role} />
          <InvitesCard workspaceId={workspace.id} />

          {workspace.role === "owner" && (
            <div className="card p-5 space-y-3 border-danger/40">
              <h3 className="font-semibold text-danger">Danger zone</h3>
              <p className="text-sm muted">
                Deleting this workspace removes all collections, requests, revisions, and run history. This cannot be undone.
              </p>
              <div>
                <LoadingButton
                  className="btn-danger"
                  pending={removeWs.isPending}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Delete ${workspace.name}?`,
                      description: "This permanently deletes every collection, request, revision and run in this workspace.",
                      danger: true,
                      confirmLabel: "Delete forever",
                    });
                    if (ok) removeWs.mutate();
                  }}
                >
                  Delete workspace
                </LoadingButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MembersCard({ workspaceId, role }: { workspaceId: string; role: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const q = useQuery({
    queryKey: ["members", workspaceId],
    queryFn: () => api.workspaces.members(workspaceId),
  });
  const remove = useMutation({
    mutationFn: (uid: string) => api.workspaces.removeMember(workspaceId, uid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", workspaceId] }),
  });
  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-semibold">Members</h3>
      <ul className="space-y-2">
        {(q.data?.members ?? []).map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-2 p-3">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar
                src={
                  m.avatar_updated_at
                    ? api.avatars.userUrl(m.id, m.avatar_updated_at)
                    : (m.picture_url ?? null)
                }
                name={m.name}
                email={m.email}
                size={36}
              />
              <div className="min-w-0">
                <div className="font-medium truncate">{m.name ?? m.email}</div>
                <div className="text-xs muted-strong truncate">{m.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="chip bg-surface-3 muted-strong">{m.role}</span>
              {role === "owner" && m.role !== "owner" && (
                <button
                  className="btn-danger"
                  onClick={async () => {
                    const ok = await confirm({ title: `Remove ${m.email}?`, danger: true });
                    if (ok) remove.mutate(m.id);
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InvitesCard({ workspaceId }: { workspaceId: string }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const list = useQuery({
    queryKey: ["invites", workspaceId],
    queryFn: () => api.workspaces.listInvites(workspaceId),
  });

  const invite = useMutation({
    mutationFn: () => api.workspaces.invite(workspaceId, email.trim().toLowerCase()),
    onSuccess: (r) => {
      setEmail("");
      qc.invalidateQueries({ queryKey: ["invites", workspaceId] });
      if (r.emailSent) {
        setMsg({ kind: "ok", text: `Invite emailed to ${r.email}.` });
      } else {
        setMsg({ kind: "ok", text: `Invite created. Share this link: ${r.url}` });
      }
    },
    onError: (e) => setMsg({ kind: "err", text: parseErr(e) }),
  });
  const resend = useMutation({
    mutationFn: (inviteId: string) => api.workspaces.resendInvite(workspaceId, inviteId),
    onSuccess: (r) => setMsg(
      r.emailSent
        ? { kind: "ok", text: "Resent." }
        : { kind: "ok", text: `Share this link: ${r.url}` }
    ),
  });
  const cancel = useMutation({
    mutationFn: (inviteId: string) => api.workspaces.cancelInvite(workspaceId, inviteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invites", workspaceId] }),
  });

  const pending = (list.data?.invites ?? []).filter((i) => !i.redeemed_at);

  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-semibold">Invite teammates</h3>
      <form
        className="flex gap-2 items-start"
        onSubmit={(e) => { e.preventDefault(); setMsg(null); invite.mutate(); }}
      >
        <input
          className="input flex-1 min-w-0"
          type="email"
          required
          placeholder="teammate@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
        <LoadingButton type="submit" className="btn-primary" pending={invite.isPending} pendingLabel="Sending…">
          Send invite
        </LoadingButton>
      </form>
      {msg && (
        <p className={"text-xs " + (msg.kind === "err" ? "text-danger" : "muted")}>{msg.text}</p>
      )}
      {pending.length > 0 && (
        <ul className="space-y-2">
          {pending.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-2 p-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{i.email}</div>
                <div className="text-xs muted-strong">Expires {new Date(i.expires_at).toLocaleDateString()}</div>
              </div>
              <div className="flex gap-2">
                <LoadingButton className="btn" pending={resend.isPending} onClick={() => resend.mutate(i.id)}>Resend</LoadingButton>
                <LoadingButton className="btn-danger" pending={cancel.isPending} onClick={() => cancel.mutate(i.id)}>Cancel</LoadingButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function parseErr(e: unknown): string {
  const m = String((e as Error).message ?? "");
  if (m.includes("invite_pending")) return "There's already a pending invite for that email.";
  if (m.includes("already_member"))  return "That person is already a member.";
  if (m.includes("bad_email"))       return "That doesn't look like a valid email.";
  return "Couldn't send the invite.";
}

function WorkspaceLogoCard({ workspace }: { workspace: Workspace }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (f: File) => api.avatars.uploadWorkspace(workspace.id, f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });
  const remove = useMutation({
    mutationFn: () => api.avatars.removeWorkspace(workspace.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });
  const src = workspace.logo_updated_at ? api.avatars.workspaceUrl(workspace.id, workspace.logo_updated_at) : null;
  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-semibold">Workspace logo</h3>
      <div className="flex items-center gap-4 flex-wrap">
        <Avatar src={src} name={workspace.name} email={workspace.name + "@workspace"} size={64} rounded="md" />
        <div className="flex flex-col gap-2 min-w-0">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              if (fileRef.current) fileRef.current.value = "";
            }}
            className="hidden"
          />
          <div className="flex gap-2 flex-wrap">
            <LoadingButton type="button" className="btn" pending={upload.isPending} onClick={() => fileRef.current?.click()}>
              Upload logo
            </LoadingButton>
            {workspace.logo_updated_at && (
              <LoadingButton type="button" className="btn-danger" pending={remove.isPending} onClick={() => remove.mutate()}>
                Remove
              </LoadingButton>
            )}
          </div>
          <p className="text-xs muted-strong">Shown next to the workspace name in the sidebar.</p>
          {upload.isError && <p className="text-xs text-danger">{String((upload.error as Error).message)}</p>}
        </div>
      </div>
    </div>
  );
}
